import type { IAddLineInput, ICart, ICartEdge, INormalizedInput, IUpdateLineInput, TAdjustCartLines } from "../interface/cart";
import type { ICustomerQuery, IOrdersEdgesNode, IOrdersLineItemsEdges } from "../interface/customer";
import type { ICaaCustomer, ICaaOrder } from "../interface/customer-account";
import type { Cart, MailingAddress } from "../types/storefront.types";

export const checkQuantityLimit = ({ cart, customer }: { cart: ICart["cart"]; customer: ICustomerQuery["customer"] }) => {
  const originalCartLines = cart.lines.edges;
  // 결과값은 { id, quantity } 형태의 배열로 관리
  let adjustedLines = originalCartLines.map(({ node }) => ({
    id: node.id,
    quantity: node.quantity,
    // 내부 로직 비교를 위해 임시로 ID들 저장 (반환 시 제거)
    _productId: node.merchandise.product.id,
    _variantId: node.merchandise.id,
  }));

  let exceeded = false;
  const productIdSet = new Set<string>();
  const variantIdSet = new Set<string>();

  const orderEdges = customer.orders.edges
    .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
    .flatMap(({ node: orderNode }) => orderNode.lineItems.edges);

  // Variant limit 먼저 적용 (각 variant별 수량 조정)
  originalCartLines.forEach(({ node }: { node: ICartEdge["node"] }) => {
    const variantLimit = parseInt(node.merchandise.product.quantityLimit?.value || "-1");
    if (variantLimit > 0 && !variantIdSet.has(node.merchandise.id)) {
      variantIdSet.add(node.merchandise.id);

      const orderedQty = orderEdges
        .filter(({ node: o }) => o.variant?.id === node.merchandise.id)
        .reduce((acc, { node: o }) => acc + o.currentQuantity, 0);

      let remainingVariantQty = Math.max(variantLimit - orderedQty, 0);

      adjustedLines = adjustedLines.map((line) => {
        if (line._variantId !== node.merchandise.id) return line;
        const allowQuantity = Math.min(line.quantity, remainingVariantQty);
        if (allowQuantity < line.quantity) exceeded = true;
        remainingVariantQty -= allowQuantity;
        return { ...line, quantity: allowQuantity };
      });
    }
  });

  // Product limit 적용 (variant limit 조정 후의 수량 기준)
  originalCartLines.forEach(({ node }: { node: ICartEdge["node"] }) => {
    const productLimit = parseInt(node.merchandise.product.quantityLimitProduct?.value || "-1");
    if (productLimit > 0 && !productIdSet.has(node.merchandise.product.id)) {
      productIdSet.add(node.merchandise.product.id);

      const orderedQty = orderEdges
        .filter(({ node: o }) => o.variant?.product.id === node.merchandise.product.id)
        .reduce((acc, { node: o }) => acc + o.currentQuantity, 0);

      let remainingProductQty = Math.max(productLimit - orderedQty, 0);

      adjustedLines = adjustedLines.map((line) => {
        if (line._productId !== node.merchandise.product.id) return line;
        const allowQuantity = Math.min(line.quantity, remainingProductQty);
        if (allowQuantity < line.quantity) exceeded = true;
        remainingProductQty -= allowQuantity;
        return { ...line, quantity: allowQuantity };
      });
    }
  });

  // 최종 결과에서 임시 필드 제거 및 수량이 0인 라인 제외
  const finalCartLines = adjustedLines.map(({ id, quantity }) => ({ id, quantity }));

  return { exceeded, adjustedCartLines: finalCartLines };
};

/**
 * classic checkQuantityLimit의 CAA 대응
 * 주문 라인 매칭에 flat `productId`/`variantId`를, 취소/환불 반영에 `refundableQuantity`를 사용
 * (classic의 currentQuantity에 대응 — checkProductLimitReachedCaa와 동일 기준)
 */
export const checkQuantityLimitCaa = ({ cart, customer }: { cart: ICart["cart"]; customer: ICaaCustomer | null }) => {
  const originalCartLines = cart.lines.edges;
  let adjustedLines = originalCartLines.map(({ node }) => ({
    id: node.id,
    quantity: node.quantity,
    _productId: node.merchandise.product.id,
    _variantId: node.merchandise.id,
  }));

  let exceeded = false;
  const productIdSet = new Set<string>();
  const variantIdSet = new Set<string>();

  const orderEdges = (customer?.orders?.edges ?? [])
    .filter(({ node }: { node: ICaaOrder }) => node.isfamilysale?.value !== "true")
    .flatMap(({ node: orderNode }) => orderNode.lineItems.edges);

  // Variant limit 먼저 적용 (각 variant별 수량 조정)
  originalCartLines.forEach(({ node }: { node: ICartEdge["node"] }) => {
    const variantLimit = parseInt(node.merchandise.product.quantityLimit?.value || "-1");
    if (variantLimit > 0 && !variantIdSet.has(node.merchandise.id)) {
      variantIdSet.add(node.merchandise.id);

      const orderedQty = orderEdges
        .filter(({ node: o }) => o.variantId === node.merchandise.id)
        .reduce((acc, { node: o }) => acc + o.refundableQuantity, 0);

      let remainingVariantQty = Math.max(variantLimit - orderedQty, 0);

      adjustedLines = adjustedLines.map((line) => {
        if (line._variantId !== node.merchandise.id) return line;
        const allowQuantity = Math.min(line.quantity, remainingVariantQty);
        if (allowQuantity < line.quantity) exceeded = true;
        remainingVariantQty -= allowQuantity;
        return { ...line, quantity: allowQuantity };
      });
    }
  });

  // Product limit 적용 (variant limit 조정 후의 수량 기준)
  originalCartLines.forEach(({ node }: { node: ICartEdge["node"] }) => {
    const productLimit = parseInt(node.merchandise.product.quantityLimitProduct?.value || "-1");
    if (productLimit > 0 && !productIdSet.has(node.merchandise.product.id)) {
      productIdSet.add(node.merchandise.product.id);

      const orderedQty = orderEdges
        .filter(({ node: o }) => o.productId === node.merchandise.product.id)
        .reduce((acc, { node: o }) => acc + o.refundableQuantity, 0);

      let remainingProductQty = Math.max(productLimit - orderedQty, 0);

      adjustedLines = adjustedLines.map((line) => {
        if (line._productId !== node.merchandise.product.id) return line;
        const allowQuantity = Math.min(line.quantity, remainingProductQty);
        if (allowQuantity < line.quantity) exceeded = true;
        remainingProductQty -= allowQuantity;
        return { ...line, quantity: allowQuantity };
      });
    }
  });

  const finalCartLines = adjustedLines.map(({ id, quantity }) => ({ id, quantity }));

  return { exceeded, adjustedCartLines: finalCartLines };
};

export const getCartLinesFromCart = (cart: Cart, customer: ICustomerQuery["customer"]) => {
  if (!cart) return [];

  const cartLines = [];

  if (cart.lines.edges.length) {
    const quantityLimitMap = getQuantityLimitObj({ customer });
    cartLines.push(...adjustCartLines(cart, quantityLimitMap));
  }

  return cartLines;
};

/**
 * 카트 만료 시, 기존 스토어의 데이터와
 * 실패한 뮤테이션의 입력값(Add, Update, Remove)을 병합하여 새 카트용 라인을 생성
 */
export const getMergedCartLines = ({
  cart,
  customer,
  inputLines = [],
  type,
}: {
  cart: Cart;
  customer: ICustomerQuery["customer"];
  inputLines?: (IAddLineInput | IUpdateLineInput | string)[];
  type: "add" | "update" | "remove";
}) => {
  // remove 요청 시 string[] → {id, quantity:0} 형태로 통일
  const normalizedInputs: INormalizedInput[] =
    type === "remove"
      ? (inputLines as string[]).map((id) => ({
          id,
          quantity: 0,
        }))
      : (inputLines as (IAddLineInput | IUpdateLineInput)[]).map((line) => ({
          id: "id" in line ? line.id : undefined,
          merchandiseId: "merchandiseId" in line ? line.merchandiseId : undefined,
          quantity: line.quantity,
        }));

  // LineID → MerchandiseID 매핑
  const lineIdToMerchandiseId = new Map<string, string>(cart.lines.edges.map((edge) => [edge.node.id, edge.node.merchandise.id]));

  // 기존 카트 라인
  const currentCartLines = getCartLinesFromCart(cart, customer);

  const mergedLines = currentCartLines
    .map((currentLine) => {
      const inputLine = normalizedInputs.find((line) => {
        if (line.id) return lineIdToMerchandiseId.get(line.id) === currentLine.merchandiseId;
        if (line.merchandiseId) return line.merchandiseId === currentLine.merchandiseId;
        return false;
      });

      // remove
      if (inputLine && inputLine.quantity === 0) return null;

      // update
      if (type === "update" && inputLine) {
        return { ...currentLine, quantity: inputLine.quantity };
      }

      // add
      if (type === "add" && inputLine) {
        return {
          ...currentLine,
          quantity: currentLine.quantity + inputLine.quantity,
        };
      }

      return currentLine;
    })
    .filter((line): line is { merchandiseId: string; quantity: number } => line !== null && line.quantity > 0);

  // 신규 상품 추가 (add 케이스)
  if (type === "add") {
    normalizedInputs.forEach((inputLine) => {
      const targetMerchandiseId = inputLine.merchandiseId || (inputLine.id ? lineIdToMerchandiseId.get(inputLine.id) : null);

      if (targetMerchandiseId && inputLine.quantity > 0 && !mergedLines.some((line) => line.merchandiseId === targetMerchandiseId)) {
        mergedLines.push({
          merchandiseId: targetMerchandiseId,
          quantity: inputLine.quantity,
        });
      }
    });
  }

  return mergedLines;
};

/**
 * @description 지난 주문 목록을 보고 남은 quantityLimit을 Map으로 저장
 */
export const getQuantityLimitObj = ({ customer }: ICustomerQuery | { customer: undefined }): Record<string, number> => {
  return customer
    ? customer.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .reduce<Record<string, number>>((accumulator, { node }) => {
          node.lineItems.edges
            .filter(({ node: itemNode }: IOrdersLineItemsEdges) => itemNode.variant)
            .forEach(({ node: lineItemsNode }: IOrdersLineItemsEdges) => {
              const product = lineItemsNode.variant?.product;
              if (product && product.quantityLimit) {
                if (!(product.id in accumulator)) {
                  accumulator[product.id] = parseInt(product.quantityLimit.value);
                }

                accumulator[product.id] -= lineItemsNode.currentQuantity;
              }
            });

          return accumulator;
        }, {})
    : {};
};

/**
 * @description 조정된 lineItems를 반환
 * cart의 lineItems를 순회하며, quantityLimitMap에 따라 quantity를 조정
 * Redux Store에 저장된 cart와 getQuantityLimit 함수가 만든 quantityLimitMap을 파라미터로 받는다
 */
export const adjustCartLines = (cart: Cart, quantityLimitMap: Record<string, number>): TAdjustCartLines => {
  return cart.lines.edges
    .map((line) => {
      let quantity = line.node.quantity;
      if (line.node.merchandise.product.id in quantityLimitMap) {
        const capacity = quantityLimitMap[line.node.merchandise.product.id];
        if (capacity === 0) {
          quantity = 0;
          // capacity보다 적게 담긴 경우에는
          // 1. quantity 유지
          // 2. capaticy 줄여주기
        } else if (quantity <= capacity) {
          quantityLimitMap[line.node.merchandise.product.id] = capacity - quantity;
          // capaticy보다 많이 담긴 경우
          // 1. quantity를 capacity만큼으로 바꿔주고
          // 2. capacity는 0으로 내려준다
        } else {
          quantity = capacity;
          quantityLimitMap[line.node.merchandise.product.id] = 0;
        }
      }

      // 사용 가능한 재고보다 많이 담은 경우에 조정
      if (line.node.merchandise.quantityAvailable && line.node.merchandise.quantityAvailable < quantity) {
        quantity = line.node.merchandise.quantityAvailable;
      }

      return { quantity, merchandiseId: line.node.merchandise.id };
    })
    .filter((line) => line.quantity > 0);
};

/**
 * @description 국가별 배송 제한 상품 체크
 * @param cart
 * @param address
 */
export const checkCountryLimitProducts = (cart: ICart["cart"], address: MailingAddress): ICartEdge[] => {
  // 현재 선택된 주소의 국가 코드를 가져온다
  const countryCode = address.countryCodeV2 as string;

  const countryLimitProducts = cart.lines.edges.filter(({ node }: { node: ICartEdge["node"] }) => {
    let availableCountries: string[];
    let unavailableCountries: string[];

    // availableCountries와 unavailableCountries에 유효하지 않은 JSON 문자열로 포함되어 실패할 경우를 대비 예외처리
    try {
      availableCountries = JSON.parse(node.merchandise.product.availableCountries?.value || "[]") as string[];
    } catch {
      availableCountries = [];
    }

    try {
      unavailableCountries = JSON.parse(node.merchandise.product.unavailableCountries?.value || "[]") as string[];
    } catch {
      unavailableCountries = [];
    }

    // 허용 목록
    if (availableCountries.length > 0) {
      return !availableCountries.includes(countryCode);
    }

    // 차단 목록
    if (unavailableCountries.length > 0) {
      return unavailableCountries.includes(countryCode);
    }

    return false;
  });

  return countryLimitProducts;
};
